import { useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Flex, Grid, VStack } from "../../../../styled-system/jsx";
import { Modal } from "../../core/basic/Modal";
import Loading from "../../core/blocks/Loading";
import { Checkbox } from "../../fields/base/Checkbox";
import { TextField } from "../../fields/base/TextField";
import { SelectedAssetsPreview, type AssetSection } from "./SelectedAssetsPreview";
import { useAppSelector } from "../../../redux/hooks";
import { Button } from "../../core/basic/Button";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "../../../zodios/client";
import { errorToast, successToast } from "../../../utils/toasts";
import { queryClient } from "../../../config/react-query";
import { type VideoGenerationTaskResult } from "../../../zodios/apiDefinitions/genai";

type Props = {
  isOpen: boolean;
  result: VideoGenerationTaskResult;
  onClose: () => void;
};

export const ReviewModal = ({ isOpen, result, onClose }: Props) => {
  const { t } = useTranslation(["advertising", "common"]);
  const [selectedGeneratedAssetIds, setSelectedGeneratedAssetIds] = useState<string[]>([]);

  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const isReviewPending = ["PENDING", "IN_PROGRESS"].includes(result.state);
  const isReviewSuccess = result.state === "SUCCESS";
  const isReviewFailed = result.state === "FAILED";
  const failureReason = result.task.state_reason;

  const selectedAssetSections: AssetSection[] = [
    { label: ($) => $.advertising["Images"], assetIds: result.task.image_ids ?? [], assetType: "IMAGE" as const },
    { label: ($) => $.advertising["Videos"], assetIds: result.task.video_ids ?? [], assetType: "VIDEO" as const },
  ];

  const generatedVideos = result.video_urls.filter((url) => url !== "failed");
  const asin = result.task.gvproduct.asin;

  const registerGeneratedVideoAssetsRequest = useMutation({
    mutationFn: async (params: Parameters<typeof apiClient.registerGeneratedVideoAssets>[0]) => {
      return await apiClient.registerGeneratedVideoAssets(params);
    },
    onSuccess: async () => {
      successToast({
        message: t(
          ($) =>
            $.advertising[
              "Successfully registered generated videos for ASIN {asin}. It may take a few minutes for the videos to be available in the media library."
            ],
          { asin },
        ),
      });
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const stringifiedQueryKey = JSON.stringify(query.queryKey);
          return stringifiedQueryKey.includes("videoGenerationRequests");
        },
      });
      onClose();
    },
    onError: (error) => {
      errorToast({
        message: t(
          ($) => $.advertising["Something went wrong while registering generated video assets. Please try again."],
        ),
        error,
      });
    },
  });

  const registerAssets = () => {
    const body = {
      video_generation_task_result_id: result.id,
      marketplace: marketPlace,
      video_ids_to_register: selectedGeneratedAssetIds,
      tags: [`trellis generative-ai ${asin.toLowerCase()}`, `${asin.toLowerCase()}`],
    };
    registerGeneratedVideoAssetsRequest.mutate(body);
  };

  const toggleGeneratedAsset = (assetId: string, isSelected: boolean) => {
    setSelectedGeneratedAssetIds((prev) => {
      if (isSelected) {
        return prev.includes(assetId) ? prev : [...prev, assetId];
      }
      return prev.filter((id) => id !== assetId);
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      title={t(($) => $.advertising["Generated Assets"])}
    >
      <VStack
        className="custom-scroll-container"
        css={{
          gap: "large",
          width: "1000px",
          maxWidth: "90vw",
          paddingX: "xlarge",
          paddingBottom: "xlarge",
          overflowY: "auto",
        }}
      >
        <TextField
          label={t(($) => $.advertising["Name"])}
          defaultValue={result.task.name}
          isDisabled
          css={{ width: "100%" }}
        />
        <SelectedAssetsPreview
          sections={selectedAssetSections}
          title={($) => $.advertising["Selected Inspirational Assets"]}
        />
        {isReviewSuccess ?
          <VStack
            gap="medium"
            alignItems="start"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="medium"
            width="100%"
            padding="large"
          >
            <div className={css({ fontSize: "large", fontWeight: "medium" })}>
              {t(
                ($) =>
                  $.advertising[
                    "Generated assets are ready for review. Select the generated videos you would like to register below."
                  ],
              )}
            </div>
            {registerGeneratedVideoAssetsRequest.isPending ?
              <Flex
                gap={"medium"}
                flexDirection={"column"}
                justifyContent={"center"}
                alignItems={"center"}
                width={"100%"}
                marginY={"large"}
              >
                <Loading fullPage={false} height={"100px"} />
                <span>
                  {t(($) => $.advertising["Assets are being registered on Amazon. This may take a minute or two."])}
                </span>
              </Flex>
            : <Grid gap="large" gridTemplateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} width="100%">
                {generatedVideos.map((generatedVideo) => {
                  const isSelected = selectedGeneratedAssetIds.includes(generatedVideo);
                  return (
                    <VStack
                      key={generatedVideo}
                      onClick={() => toggleGeneratedAsset(generatedVideo, !isSelected)}
                      cursor={"pointer"}
                      gap="small"
                      alignItems="start"
                      borderRadius={"5px"}
                      padding={"small"}
                      backgroundColor={selectedGeneratedAssetIds.includes(generatedVideo) ? "gray.100" : "inherit"}
                    >
                      <div
                        className={css({
                          position: "relative",
                          aspectRatio: "16 / 9",
                          borderRadius: "small",
                          width: "100%",
                          backgroundColor: "gray.100",
                          overflow: "hidden",
                        })}
                      >
                        <video
                          controls
                          preload="metadata"
                          controlsList="nodownload noremoteplayback"
                          disablePictureInPicture
                          className={css({
                            display: "block",
                            inset: 0,
                            position: "absolute",
                            objectFit: "cover",
                            width: "100%",
                            height: "100%",
                          })}
                        >
                          <source src={generatedVideo} type="video/mp4" />
                        </video>
                      </div>
                      <Flex justifyContent={"flex-end"} width={"100%"}>
                        <Checkbox
                          value={isSelected}
                          onChange={(value) => {
                            toggleGeneratedAsset(generatedVideo, value);
                          }}
                        />
                      </Flex>
                    </VStack>
                  );
                })}
              </Grid>
            }
            <Flex gap={"xlarge"} justifyContent={"center"} width={"100%"}>
              <Button onPress={() => onClose()} variant={"secondary"}>
                {t(($) => $.common.actions.Cancel)}
              </Button>
              <Button
                onPress={() => registerAssets()}
                isDisabled={!selectedGeneratedAssetIds.length || registerGeneratedVideoAssetsRequest.isPending}
              >
                {t(($) => $.advertising["Register Assets"])}
              </Button>
            </Flex>
          </VStack>
        : isReviewPending ?
          <VStack gap="medium" width="100%">
            <p>
              {t(
                ($) =>
                  $.advertising[
                    "Video generation is in progress. This typically takes 5 to 10 minutes. Check back soon..."
                  ],
              )}
            </p>
            <Loading fullPage={false} height={"100px"} />
            <Flex gap={"xxlarge"} justifyContent={"center"} width={"100%"} paddingTop={"xlarge"}>
              <Button onPress={() => onClose()}>{t(($) => $.common.actions.Close)}</Button>
            </Flex>
          </VStack>
        : isReviewFailed ?
          <VStack gap="medium" alignItems="start" width="100%">
            <div
              className={css({
                border: "1px solid",
                borderColor: "red.200",
                borderRadius: "medium",
                width: "100%",
                padding: "large",
                color: "red.800",
                backgroundColor: "red.50",
              })}
            >
              {failureReason ??
                t(($) => $.advertising["Video generation failed. Please try again or contact customer success."])}
            </div>
            <Flex gap={"xxlarge"} justifyContent={"center"} width={"100%"} paddingTop={"xlarge"}>
              <Button onPress={() => onClose()}>{t(($) => $.common.actions.Close)}</Button>
            </Flex>
          </VStack>
        : null}
      </VStack>
    </Modal>
  );
};
